import axios from 'axios';
import { cacheManager, CACHE_PRESETS } from '../utils/cacheManager';
import { ingestResponse } from './mockModeBus';
import { isReadOnlyRole } from '../hooks/useIsReadOnly';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 second timeout
});

// ──────────────────────────────────────────────────────────────────
// BUG 19.4 (Phase 7.4, Jul 2026) — refresh-token rotation helpers.
//
// Mirrors the Flutter interceptor pattern: on 401, single-flight
// rotate the refresh token via /auth/refresh, retry the original
// request ONCE with the new access token. On 4xx from refresh, wipe
// all auth storage + redirect to login. On network/5xx error from
// refresh, propagate the original 401 without clearing (user can
// retry when online).
//
// Single-flight: a module-level Promise gates concurrent 401s. Three
// parallel API calls all returning 401 will share ONE /auth/refresh
// call; without this, the server-side reuse-detection family-kill
// would trigger on the 2nd parallel rotation.
//
// Storage layout in localStorage:
//   Admin (web)     : token, refresh_token, refresh_expires_at, user
//   Employee portal : emp_token, emp_refresh_token, emp_refresh_expires_at, emp_data
// ──────────────────────────────────────────────────────────────────
let _refreshInFlight = null;

function readCompanyIdForRefresh(isEmployeePage) {
  try {
    if (isEmployeePage) {
      const empStr = localStorage.getItem('emp_data');
      if (empStr) {
        const emp = JSON.parse(empStr);
        if (emp?.companyId) return emp.companyId;
      }
    } else {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.companyId) return user.companyId;
      }
    }
  } catch { /* malformed JSON — fall through */ }
  return null;
}

/**
 * BUG 19.4 — single-flight refresh. Multiple concurrent 401s share
 * the SAME in-flight refresh promise. Returns the new access token
 * on success, or null on failure.
 *
 * On a 4xx from /auth/refresh (INVALID / REUSE_DETECTED / MISSING),
 * we wipe all stored auth and let the caller redirect to /login.
 * On a network / 5xx error from /auth/refresh, we leave storage
 * untouched so the user can retry when the network recovers.
 */
async function singleFlightRefresh({ refreshToken, companyId, accessTokenKey, refreshTokenKey }) {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const fullUrl = `${BACKEND_URL}/api/auth/refresh`;
      const resp = await axios.post(fullUrl, { refreshToken, companyId }, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      const body = resp.data;
      if (body?.success === true && body.data?.token) {
        const newToken = body.data.token;
        const newRefresh = body.data.refreshToken;
        const newExpires = body.data.refreshExpiresAt;
        localStorage.setItem(accessTokenKey, newToken);
        if (newRefresh) {
          localStorage.setItem(refreshTokenKey, newRefresh);
          if (newExpires) {
            localStorage.setItem(
              refreshTokenKey === 'emp_refresh_token' ? 'emp_refresh_expires_at' : 'refresh_expires_at',
              newExpires,
            );
          }
        }
        return newToken;
      }
      // success=false in body — treat as invalid.
      _clearAuthStorage(accessTokenKey, refreshTokenKey);
      return null;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400 || status === 401) {
        _clearAuthStorage(accessTokenKey, refreshTokenKey);
        return null;
      }
      // Network / 5xx — preserve tokens, return null so caller
      // propagates the original 401. User can retry when online.
      return null;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

function _clearAuthStorage(accessTokenKey, refreshTokenKey) {
  localStorage.removeItem(accessTokenKey);
  localStorage.removeItem(refreshTokenKey);
  if (refreshTokenKey === 'emp_refresh_token') {
    localStorage.removeItem('emp_refresh_expires_at');
    localStorage.removeItem('emp_data');
  } else {
    localStorage.removeItem('refresh_expires_at');
    localStorage.removeItem('user');
  }
}

// Enhanced cache configuration for different endpoints
const CACHE_CONFIG = {
  // Dashboard - fast refresh, memory only
  '/dashboard/today-summary': { ...CACHE_PRESETS.DASHBOARD },
  '/dashboard/weekly-trend': { ttl: 60000, staleTime: 120000, persist: false },
  '/dashboard/kiosk-health': { ttl: 30000, staleTime: 60000, persist: false },
  '/dashboard/live-punch-feed': { ...CACHE_PRESETS.REALTIME },
  '/dashboard/ot-utilization': { ttl: 60000, staleTime: 120000, persist: false },
  
  // Reports - longer cache, persist
  '/reports/overview': { ...CACHE_PRESETS.REPORTS },
  '/reports/weekly-trend': { ...CACHE_PRESETS.REPORTS },
  '/reports/monthly-trend': { ttl: 180000, staleTime: 600000, persist: true },
  '/discipline': { ttl: 120000, staleTime: 300000, persist: true },
  
  // Heavy data - persist to localStorage
  '/employees': { ...CACHE_PRESETS.EMPLOYEES },
  '/attendance/daily': { ttl: 30000, staleTime: 120000, persist: true },
  
  // Static/semi-static data - long cache
  '/shifts': { ...CACHE_PRESETS.STATIC },
  '/departments': { ...CACHE_PRESETS.STATIC },
  '/units': { ...CACHE_PRESETS.STATIC },
  '/contractors': { ttl: 180000, staleTime: 600000, persist: true },
  '/settings': { ttl: 300000, staleTime: 900000, persist: true },
};

/**
 * Get cache config for endpoint
 */
function getCacheConfig(url) {
  for (const [pattern, config] of Object.entries(CACHE_CONFIG)) {
    if (url.includes(pattern)) return config;
  }
  return null;
}

// Enhanced API with advanced caching
const enhancedApi = {
  ...api,
  
  /**
   * GET with stale-while-revalidate caching
   */
  get: async (url, config = {}) => {
    const cacheConfig = getCacheConfig(url);
    const params = config.params || {};
    
    // Skip cache if explicitly requested
    if (config.skipCache || !cacheConfig) {
      return api.get(url, config);
    }
    
    const cacheKey = cacheManager.generateKey(url, params);
    
    // Check cache first
    const cached = cacheManager.get(url, params, {
      staleTime: cacheConfig.staleTime,
      persist: cacheConfig.persist
    });
    
    // Return fresh data immediately
    if (cached?.isFresh) {
      return cached.data;
    }
    
    // Stale data - return immediately but revalidate in background
    if (cached?.isStale) {
      // Background revalidation (don't await)
      cacheManager.dedupedFetch(cacheKey, async () => {
        try {
          const response = await api.get(url, config);
          if (response?.success !== false) {
            cacheManager.set(url, params, response, cacheConfig.ttl, {
              persist: cacheConfig.persist
            });
          }
          return response;
        } catch (e) {
          console.warn('Background revalidation failed:', url);
          return null;
        }
      });
      
      return cached.data;
    }
    
    // No cache - fetch with deduplication
    const response = await cacheManager.dedupedFetch(cacheKey, () => api.get(url, config));
    
    // Cache successful response
    if (response?.success !== false) {
      cacheManager.set(url, params, response, cacheConfig.ttl, {
        persist: cacheConfig.persist
      });
    }
    
    return response;
  },
  
  // POST/PUT/DELETE invalidate related caches
  post: async (url, data, config) => {
    const response = await api.post(url, data, config);
    if (response?.success) {
      invalidateRelatedCaches(url);
    }
    return response;
  },
  
  put: async (url, data, config) => {
    const response = await api.put(url, data, config);
    if (response?.success) {
      invalidateRelatedCaches(url);
    }
    return response;
  },
  
  patch: async (url, data, config) => {
    const response = await api.patch(url, data, config);
    if (response?.success) {
      invalidateRelatedCaches(url);
    }
    return response;
  },
  
  delete: async (url, config) => {
    const response = await api.delete(url, config);
    if (response?.success) {
      invalidateRelatedCaches(url);
    }
    return response;
  },

  // Force refresh (skip cache)
  refresh: (url, config = {}) => enhancedApi.get(url, { ...config, skipCache: true }),
  
  // Clear all caches
  clearCache: () => cacheManager.clear(),
  
  // Invalidate specific pattern
  invalidateCache: (pattern) => cacheManager.invalidatePattern(pattern),
  
  // Get cache stats
  getCacheStats: () => cacheManager.getStats(),
  
  // Prefetch data
  prefetch: (url, config = {}) => {
    const cacheConfig = getCacheConfig(url);
    if (cacheConfig) {
      cacheManager.prefetch(url, () => api.get(url, config), cacheConfig.ttl, {
        persist: cacheConfig.persist
      });
    }
  }
};

/**
 * Invalidate caches related to a mutation endpoint
 */
function invalidateRelatedCaches(url) {
  if (url.includes('/employee')) {
    cacheManager.invalidatePattern('/employees');
    cacheManager.invalidatePattern('/dashboard');
  }
  if (url.includes('/attendance') || url.includes('/punch')) {
    cacheManager.invalidatePattern('/dashboard');
    cacheManager.invalidatePattern('/reports');
    cacheManager.invalidatePattern('/attendance');
  }
  if (url.includes('/shift')) {
    cacheManager.invalidatePattern('/shifts');
  }
  if (url.includes('/settings')) {
    cacheManager.invalidatePattern('/settings');
    cacheManager.invalidatePattern('/custom-categories');
  }
  // BUG FIX: Department, contractor, holiday, leave cache invalidation
  if (url.includes('/department')) {
    cacheManager.invalidatePattern('/departments');
    cacheManager.invalidatePattern('/dashboard');
  }
  if (url.includes('/contractor')) {
    cacheManager.invalidatePattern('/contractors');
    cacheManager.invalidatePattern('/dashboard');
  }
  if (url.includes('/holiday')) {
    cacheManager.invalidatePattern('/holidays');
    cacheManager.invalidatePattern('/dashboard');
  }
  if (url.includes('/leave')) {
    cacheManager.invalidatePattern('/leaves');
    cacheManager.invalidatePattern('/dashboard');
  }
  if (url.includes('/unit')) {
    cacheManager.invalidatePattern('/units');
  }
  if (url.includes('/kiosk')) {
    cacheManager.invalidatePattern('/kiosks');
    cacheManager.invalidatePattern('/dashboard');
  }
  // ─── Phase A hotfix (Jul 2025) — BUG 6 + preventive coverage ───
  // Previously POST/PUT/DELETE on these surfaces left the cached GET
  // lists stale. The `/discipline` cache pattern in CACHE_CONFIG
  // (api.js L136) is `persist: true` with a 5-minute stale-time, so a
  // newly-created Discipline Rule was invisible for up to 5 minutes /
  // until manual cache clear. We invalidate every related cache key
  // here so the matching `enhancedApi.get` falls through to a fresh
  // server fetch on the next call. Additive — no TTL / persist
  // behaviour changed elsewhere.
  if (url.includes('/workforce-policy') || url.includes('/discipline')) {
    cacheManager.invalidatePattern('/workforce-policy');
    cacheManager.invalidatePattern('/discipline');
  }
  if (url.includes('/visitor')) {
    cacheManager.invalidatePattern('/visitor');
    cacheManager.invalidatePattern('/visitors');
    cacheManager.invalidatePattern('/visitor-engine');
  }
  if (url.includes('/expense')) {
    cacheManager.invalidatePattern('/expense');
    cacheManager.invalidatePattern('/expenses');
    cacheManager.invalidatePattern('/dashboard');
  }
  if (url.includes('/reimburs')) {
    // Matches both `/reimbursement` and `/reimbursements`.
    cacheManager.invalidatePattern('/reimburs');
    cacheManager.invalidatePattern('/dashboard');
  }
  // Phase E (Jul 2026) — Bug 10: invalidate the payroll config cache
  // so a PUT /payroll/config immediately reflects on the next GET
  // (otherwise the FE form would render stale typed-column values
  // alongside the freshly-merged extra_settings keys).
  if (url.includes('/payroll')) {
    cacheManager.invalidatePattern('/payroll');
  }
  if (url.includes('/recruitment')) {
    cacheManager.invalidatePattern('/recruitment');
    cacheManager.invalidatePattern('/me/feature-flags');
  }
  if (url.includes('/feature-flag')) {
    cacheManager.invalidatePattern('/feature-flag');
    cacheManager.invalidatePattern('/me/feature-flags');
  }
}

// Request interceptor to add auth token and company context
api.interceptors.request.use(
  (config) => {
    // ── Feb 2026 (Round 12.1) — Viewer-role write guard ──
    // Defense-in-depth: even if a write button slips past the global
    // <Button> wrapper (custom anchor, native <form onSubmit>, code
    // path that constructs the request from outside React), abort
    // here before the request hits the wire. Backend's
    // viewerReadOnlyGuard middleware is the real enforcement, but
    // shaving the trip avoids a confusing 403 toast for the
    // legitimately-blocked Viewer user. GET / HEAD / OPTIONS pass.
    const method = String(config.method || 'GET').toUpperCase();
    const isWrite = !['GET', 'HEAD', 'OPTIONS'].includes(method);
    if (isWrite && isReadOnlyRole()) {
      // Skip the guard for the auth flows themselves (login is a POST
      // but the user can't be "logged in as Viewer" before logging
      // in). The skipAuth list below already exempts these paths;
      // mirror it here for explicit clarity.
      const skipGuard = ['/auth/login', '/auth/mobile-login', '/auth/logout', '/password-reset/'].some(
        p => config.url?.includes(p)
      );
      if (!skipGuard) {
        const err = new Error('Your account is read-only and cannot modify data.');
        err.response = {
          status: 403,
          data: { success: false, code: 'READ_ONLY_ROLE', message: err.message, clientBlocked: true },
        };
        err.config = config;
        return Promise.reject(err);
      }
    }

    // Don't add auth token for login/reset endpoints
    const skipAuth = ['/auth/login', '/auth/mobile-login', '/password-reset/'].some(
      path => config.url?.includes(path)
    );
    if (!skipAuth) {
      const token = localStorage.getItem('token') || localStorage.getItem('emp_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    const companyId = localStorage.getItem('selectedCompanyId');
    if (companyId) {
      config.headers['X-Company-Id'] = companyId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Phase M6.0 — latch mock-mode flag if the backend marked this
    // response as mock. Banner + sonner-prefix subscribe to the bus.
    ingestResponse(response?.data);
    return response.data;
  },
  async (error) => {
    // Phase M6.0 — inspect error envelopes too.
    ingestResponse(error?.response?.data);
    // ISSUE-003: first-load/first-switch transient errors — auto-retry ONCE
    // for GET requests on network errors, timeouts, and 502/503/504. This
    // covers the "Failed to Load on first attempt, works on second" pattern
    // caused by cold tenant DB pools and in-flight auth token refresh.
    const cfg = error.config;
    const isRetriable = cfg
      && (cfg.method || 'get').toLowerCase() === 'get'
      && !cfg._retried
      && (
        error.code === 'ECONNABORTED'
        || error.code === 'ERR_NETWORK'
        || [502, 503, 504].includes(error.response?.status)
        || (error.message || '').toLowerCase().includes('network')
      );
    if (isRetriable) {
      cfg._retried = true;
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const resp = await api(cfg);
        return resp;
      } catch (_) {
        // fall through to the normal error path below
      }
    }

    if (error.response?.status === 401) {
      const path = window.location.pathname;
      const isLoginPage = path === '/login' || path === '/employee/login' || path.startsWith('/forgot-password');
      const isKiosk = path.startsWith('/kiosk');
      // TV Dashboard (`/tv/:companyId`) is a wall-mounted display with no
      // keyboard and no user session. Hitting an auth-protected endpoint
      // (e.g. /settings from useCompanySettings) must NOT kick the screen
      // into /login — it would strand the TV until a human logs in manually.
      const isTv = path.startsWith('/tv');

      // BUG 19.4 (Phase 7.4, Jul 2026) — try to refresh the access
      // token BEFORE forcing a redirect to /login. The /auth/refresh
      // call is gated by:
      //   • request not on an auth/password-reset path (avoid loop)
      //   • refresh token is in localStorage
      //   • request hasn't already been retried after a refresh
      //   • not on login/kiosk/TV pages (these have their own flows)
      const reqUrl = error.config?.url || '';
      const isAuthPath = reqUrl.startsWith('/auth/') || reqUrl.startsWith('/password-reset/');
      const isEmployeePage = path.startsWith('/employee');
      const refreshTokenKey = isEmployeePage ? 'emp_refresh_token' : 'refresh_token';
      const accessTokenKey  = isEmployeePage ? 'emp_token' : 'token';
      const companyId = readCompanyIdForRefresh(isEmployeePage);
      const refreshToken = localStorage.getItem(refreshTokenKey);
      const alreadyRetried = error.config?._refreshRetried === true;

      if (!isLoginPage && !isKiosk && !isTv && !isAuthPath
          && !alreadyRetried && refreshToken && companyId) {
        try {
          const newAccess = await singleFlightRefresh({ refreshToken, companyId, accessTokenKey, refreshTokenKey });
          if (newAccess) {
            // Retry the original request once with the new access token.
            error.config._refreshRetried = true;
            error.config.headers = error.config.headers || {};
            error.config.headers.Authorization = `Bearer ${newAccess}`;
            return api(error.config);
          }
        } catch (refreshErr) {
          // singleFlightRefresh already cleaned up storage on 4xx.
          // Fall through to the original 401 redirect.
        }
      }

      if (!isLoginPage && !isKiosk && !isTv) {
        if (isEmployeePage) {
          localStorage.removeItem('emp_token');
          localStorage.removeItem('emp_data');
          localStorage.removeItem('emp_refresh_token');
          localStorage.removeItem('emp_refresh_expires_at');
          window.location.href = '/employee/login';
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('refresh_expires_at');
          window.location.href = '/login';
        }
      }
    }
    // Surface a clear message for 503 (company DB temporarily unavailable)
    if (error.response?.status === 503) {
      const msg = error.response?.data?.message || 'Service temporarily unavailable. Please try again.';
      error.userMessage = msg;
    }
    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════════════════════════════════════
// ArcFace Microservice API (Isolated Python Service)
// ════════════════════════════════════════════════════════════════════════════

export const arcFaceApi = {
  /** Fetch preprocessing config (no auth needed) */
  getConfig: () => enhancedApi.get('/ankastra/faces/config'),
  
  /** Health check */
  getHealth: () => enhancedApi.get('/ankastra/faces/health'),
  
  /** Resource governor status */
  getGovernor: () => enhancedApi.get('/ankastra/faces/governor'),
  
  /** 5-step face validation */
  validate: (base64Image) => enhancedApi.post('/ankastra/faces/validate', { image: base64Image }),
  
  /** Identify face 1:N */
  identify: (base64Image, deviceId, capturedAt) => enhancedApi.post('/ankastra/faces/identify', {
    image: base64Image,
    face_image_base64: base64Image,
    device_id: deviceId,
    captured_at: capturedAt
  }),
  
  /** Enroll: 5 photos → 1 averaged ArcFace embedding (512-dim) */
  enroll: (employeeId, images) => enhancedApi.post('/ankastra/faces/enroll', {
    employee_id: employeeId,
    employeeId: employeeId,
    images: images
  }),
  
  /** 1:1 verification */
  verify: (base64Image, employeeId) => enhancedApi.post('/ankastra/faces/verify', {
    image: base64Image,
    face_image_base64: base64Image,
    employee_id: employeeId
  }),

  /** Get enrolled face images for an employee */
  getEnrolled: (employeeId) => enhancedApi.get(`/ankastra/faces/enrolled/${employeeId}`),

  /** Delete a specific enrolled face image */
  deleteEnrolledImage: (employeeId, index) => enhancedApi.delete(`/ankastra/faces/enrolled/${employeeId}/image/${index}`),

  /** Full unenroll — delete ALL face data for an employee */
  unenrollAll: (employeeId) => enhancedApi.delete(`/ankastra/faces/enrolled/${employeeId}`),
};


export default enhancedApi;
